import { Component, CUSTOM_ELEMENTS_SCHEMA, OnInit } from '@angular/core';
import { LocalStorageService } from '../../services/local-storage.service';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { NgxSpinnerComponent, NgxSpinnerService } from 'ngx-spinner';
import { finalize, interval, Subscription, switchMap, timer } from 'rxjs';
import { TracerService } from '../../services/tracer.service';
import { Request, Session, SessionFilters, SessionStatus, Trace } from '../../models/tracer.model';
import { ToastrService } from 'ngx-toastr';
import { DecimalPipe, NgClass, NgOptimizedImage } from '@angular/common';
import 'cally';
import { NgxPaginationModule } from 'ngx-pagination';
import { GetConsumerPipe } from '../../pipes/get-consumer-pipe';
import { GetAgencyPipe } from '../../pipes/get-agency-pipe';
import { FormatDatePipe } from '../../pipes/format-date-pipe';
import { GetProvidersPipe } from '../../pipes/get-providers-pipe';
import { CollapseProvidersPipe } from '../../pipes/collapse-providers-pipe';
import { IsProviderCallbackPipe } from '../../pipes/is-provider-callback-pipe';
import { GetStatusPipe } from '../../pipes/get-status-pipe';
import { GetSessionStatusPipe } from '../../pipes/get-session-status-pipe';
import { SessionProvidersPipe } from '../../pipes/session-providers-pipe';
import { SessionLastActionPipe } from '../../pipes/session-last-action-pipe';
import { SessionUserService } from '../../services/session-user.service';
import { INBOUND_METHODS } from '../../models/methods';

@Component({
  selector: 'app-home',
  imports: [
    FormsModule,
    NgxSpinnerComponent,
    NgClass,
    NgOptimizedImage,
    DecimalPipe,
    NgxPaginationModule,
    GetConsumerPipe,
    GetAgencyPipe,
    FormatDatePipe,
    GetProvidersPipe,
    CollapseProvidersPipe,
    IsProviderCallbackPipe,
    GetStatusPipe,
    GetSessionStatusPipe,
    SessionProvidersPipe,
    SessionLastActionPipe,
    RouterLink,
  ],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  templateUrl: './home.html',
  styleUrl: './home.scss',
})
export class Home implements OnInit {
  consumer: string | null = null;
  currentPage = 1;
  // perPage default is 20 (was 10) to leave headroom for client-side
  // filtering: prepareCollapsedRequests() drops whole sessions whose
  // every request is IsCollapsed (background traffic). With 10 from the
  // backend, the visible count after filtering routinely dropped to 5–7
  // — operators wanted "at least 10" on screen. 20 reliably surfaces
  // 10+ post-filter rows in typical traffic without overwhelming the
  // server (DISTINCT-on-(session_id, created_at) cost is sub-linear in
  // perPage at this size).
  perPage = 20;
  // perPageMin clamps the parsed `per_page` query param so a stale URL
  // can't force a tiny page below the threshold the UX expects.
  private static readonly perPageMin = 10;
  searchModel = {
    application: '',
    consumer: '',
    provider: '',
    method: '',
    status: '',
    agent: '',
    agency: '',
    api_version: '',
    date_from: '',
    date_to: '',
    important: false,
    background: false,
    // anonymous → server-side opt-in to include sessions composed entirely
    // of auth-failed (Consumer="unknown") traces. Off by default so the
    // default session list isn't dominated by buggy clients hammering
    // sandbox with bad credentials. See AGWTracer/tracer page_controller.go
    // GetIndex for the matching server-side filter.
    anonymous: false,
  };
  loaded: boolean = false;
  showVersion = {};
  sessions: Session[] = [];
  sessionsCount = 0;
  preparedSessionData: Session[] = [];
  collapsedIdxOpened: number[] = [];
  isSessionVisible: Record<number, boolean> = {};
  isDateFromPopoverOpen = false;
  isDateToPopoverOpen = false;
  enableLiveUpdate = false;
  intervalSubscription?: Subscription;
  // The v2 API operations — what the sessions filter matches. Generated from
  // agw-api-v2 (scripts/gen-methods.sh); v1's NDC names match nothing in v2.
  allowedMethods: readonly string[] = INBOUND_METHODS;
  filteredMethods: string[] = [...this.allowedMethods];
  showOptions = false;
  selectedRealm: string | null = null;
  highlightedMethodIndex = -1;

  // Stats card state for the landing view. Null before resolution so the
  // template can show a neutral placeholder.
  //
  // v1 also showed sessions_replayed / requests_replayed; replay is gone, so
  // those counters went with it.
  stats: {
    sessions: number;
    requests: number;
  } | null = null;

  // Login tab state — drives which login button gets the primary
  // styling and which realm gets passed to selectRealm() on submit.
  loginTab: 'agents' | 'admins' = 'agents';

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private ls: LocalStorageService,
    private spinner: NgxSpinnerService,
    private toastr: ToastrService,
    private tracerService: TracerService,
    protected keycloak: SessionUserService,
  ) {}

  ngOnInit() {
    if (!!this.keycloak.authenticated) {
      this.consumer = this.ls.getItem('consumer');
      this.parseURL();
      this.search();
    } else {
      this.selectedRealm = this.ls.getItem('selectedRealm');
      if (this.selectedRealm) {
        this.keycloak.login();
        this.ls.removeItem('selectedRealm');
      }
      // Lazy-fetch the public stats for the landing view. Endpoint is
      // server-cached for 60s so refresh-spamming the page is cheap.
      this.tracerService.getTotals().subscribe({
        next: (stats) => (this.stats = stats),
        error: () => (this.stats = null),
      });
    }
  }

  parseURL(): void {
    const q = this.route.snapshot.queryParams;

    if (q['application']) this.searchModel.application = q['application'];
    if (q['consumer']) this.searchModel.consumer = q['consumer'];
    if (q['provider']) this.searchModel.provider = q['provider'];
    if (q['method']) this.searchModel.method = q['method'];
    if (q['status']) this.searchModel.status = q['status'];
    if (q['agent']) this.searchModel.agent = q['agent'];
    if (q['agency']) this.searchModel.agency = q['agency'];
    if (q['api_version']) this.searchModel.api_version = q['api_version'];
    if (q['date_from']) this.searchModel.date_from = q['date_from'];
    if (q['date_to']) this.searchModel.date_to = q['date_to'];
    if (q['important']) this.searchModel.important = q['important'];
    if (q['background']) this.searchModel.background = q['background'];
    if (q['anonymous']) this.searchModel.anonymous = q['anonymous'] === 'true';
    if (q['current_page']) this.currentPage = parseInt(q['current_page'], 10);
    if (q['per_page']) {
      const parsed = parseInt(q['per_page'], 10);
      this.perPage = Number.isFinite(parsed) ? Math.max(parsed, Home.perPageMin) : this.perPage;
    }
  }

  buildURL(): void {
    const q: any = {};

    const s = this.searchModel;

    if (s.application) q.application = s.application;
    if (s.consumer) q.consumer = s.consumer;
    if (s.provider) q.provider = s.provider;
    if (s.method) q.method = s.method;
    if (s.status) q.status = s.status;
    if (s.agent) q.agent = s.agent;
    if (s.agency) q.agency = s.agency;
    if (s.api_version) q.api_version = s.api_version;
    if (s.date_from) q.date_from = s.date_from;
    if (s.date_to) q.date_to = s.date_to;
    if (s.important) q.important = s.important;
    if (s.background) q.background = s.background;
    if (s.anonymous) q.anonymous = s.anonymous;
    if (this.currentPage) q.current_page = this.currentPage;
    if (this.perPage) q.per_page = this.perPage;

    this.router
      .navigate(['/'], {
        queryParams: q,
      })
      .catch((err) => {
        // optional error handling
        console.error('Navigation error:', err);
      });
  }

  // The cursor each page starts from, by page number. The API pages by
  // keyset, so page N is reachable only through the cursor page N-1 returned;
  // page 1 needs none. Valid only for the filters they were fetched with.
  private pageCursors: (string | undefined)[] = [];
  private cursorFilters = '';

  /** The form as API filters. Every field used to be dropped. */
  sessionFilters(): SessionFilters {
    const s = this.searchModel;
    const text = (v: unknown) => (typeof v === 'string' ? v.trim() : '');
    // The form's dates are UTC days, matching the UTC times the list shows;
    // the API wants instants (RFC 3339), from inclusive and to exclusive — so
    // "to" is the start of the next day.
    const dayStart = (d: string, plusDays = 0) => {
      const t = new Date(`${d}T00:00:00Z`);
      if (Number.isNaN(t.getTime())) return undefined;
      t.setUTCDate(t.getUTCDate() + plusDays);
      return t.toISOString();
    };
    return {
      application: text(s.application),
      consumer: text(s.consumer),
      agency: text(s.agency),
      agent: text(s.agent),
      method: text(s.method),
      provider: text(s.provider),
      status: (text(s.status) || undefined) as SessionStatus | undefined,
      anonymous: s.anonymous === true,
      from: s.date_from ? dayStart(s.date_from) : undefined,
      to: s.date_to ? dayStart(s.date_to, 1) : undefined,
      limit: this.perPage,
    };
  }

  search() {
    const filters = this.sessionFilters();
    const key = JSON.stringify(filters);
    let page = Math.max(this.currentPage, 1);
    // New filters, or a page whose cursor is unknown (e.g. a reloaded link to
    // page 5): start again from page 1.
    if (key !== this.cursorFilters || (page > 1 && !this.pageCursors[page])) {
      this.pageCursors = [];
      this.cursorFilters = key;
      page = 1;
    }
    this.currentPage = page;
    const request: SessionFilters = { ...filters, cursor: page > 1 ? this.pageCursors[page] : undefined };

    this.buildURL();
    this.loaded = false;
    this.spinner.show();

    this.showVersion = {};
    this.sessions = [];

    timer(250)
      .pipe(
        switchMap(() => this.tracerService.getTracerData(request)),
        finalize(() => {
          this.loaded = true;
          this.spinner.hide();
        }),
      )
      .subscribe({
        next: (res) => {
          if (!res.error) {
            this.sessions = res.sessions;
            this.sortSessionsByUpdatedAt();

            this.pageCursors[page + 1] = res.next_cursor;

            if (res.sessions) {
              // The API returns no total; a next cursor means at least one
              // more page.
              if (!res.next_cursor) {
                this.sessionsCount = (page - 1) * this.perPage + res.sessions.length;
              } else {
                this.sessionsCount = page * this.perPage + 1;
              }

              this.prepareCollapsedRequests();
            }
          } else {
            this.preparedSessionData = [];
            this.toastr.error(res.error, 'Error');
          }
        },
        error: (error) => {
          this.preparedSessionData = [];
          const errorMessage =
            typeof error?.error?.error === 'string'
              ? error.error.error
              : 'Failed to load tracer data';

          this.toastr.error(errorMessage, 'Error');
        },
      });
  }

  prepareCollapsedRequests(): void {
    this.preparedSessionData = [];
    this.collapsedIdxOpened = [];

    let collapsedIdx = 0;
    const filteredSessions: Session[] = [];

    for (const s of this.sessions) {
      const filteredRequests: Request[] = [];

      for (const r of s.requests || []) {
        if (this.searchModel.background || !r.IsCollapsed) {
          filteredRequests.push(r);
        }
      }

      if (filteredRequests.length > 0) {
        filteredSessions.push({
          ...s,
          requests: filteredRequests,
        });
      }
    }

    filteredSessions.forEach((session, index) => {
      const preparedRequests: any[] = [];
      let i = 0;
      const n = session.requests.length;

      while (i < n) {
        const current = session.requests[i];

        if (current?.IsCollapsed && !this.isErrorInTracer(current.Traces || [])) {
          let j = i + 1;
          while (
            j < n &&
            session.requests[j]?.IsCollapsed &&
            !this.isErrorInTracer(session.requests[j].Traces || [])
          ) {
            j++;
          }

          if (j - i > 1) {
            const collapsedCountInfo: Record<string, number> = {};
            const collapsedRequests: Request[] = [];

            for (let k = i; k < j; k++) {
              const req = session.requests[k];
              if (req) {
                collapsedRequests.push(req);
                collapsedCountInfo[req.Method] = (collapsedCountInfo[req.Method] || 0) + 1;
              }
            }

            preparedRequests.push({
              isCollapsedArrayRequests: true,
              collapsedIdx: collapsedIdx,
              collapsedCountInfo: this.prepareCollapsedCountInfo(collapsedCountInfo),
              requests: collapsedRequests,
            });

            collapsedIdx++;
            i = j;
            continue;
          }
        }

        preparedRequests.push(current);
        i++;
      }

      this.preparedSessionData.push({
        ...session,
        requests: preparedRequests,
      });

      this.isSessionVisible[index] = false;
    });
  }

  prepareCollapsedCountInfo(collapsedCountInfo: Record<string, number>): string {
    const parts = Object.entries(collapsedCountInfo).map(
      ([method, count]) => `${count} ${method} call${count > 1 ? 's' : ''}`,
    );
    return `(${parts.join(', ')})`;
  }

  private sortSessionsByUpdatedAt(): void {
    if (!this.sessions || this.sessions.length === 0) return;

    this.sessions.sort((a, b) => {
      return b.updated_at.localeCompare(a.updated_at);
    });
  }

  isErrorInTracer(traces: Trace[] = []): boolean {
    return traces.some((trace) => trace?.Status === 'error' || trace?.Status === 'http_error');
  }

  reset() {
    this.searchModel = {
      application: '',
      consumer: '',
      provider: '',
      method: '',
      status: '',
      agent: '',
      agency: '',
      api_version: '',
      date_from: '',
      date_to: '',
      important: false,
      background: false,
      anonymous: false,
    };
  }

  onDateFromPicked(event: Event) {
    const calendar = event.target as any;
    this.searchModel.date_from = calendar.value;
    this.isDateFromPopoverOpen = false;
  }

  onDateToPicked(event: Event) {
    const calendar = event.target as any;
    this.searchModel.date_to = calendar.value;
    this.isDateToPopoverOpen = false;
  }

  toggleLiveUpdate() {
    if (this.enableLiveUpdate) {
      this.intervalSubscription?.unsubscribe();
    } else {
      this.intervalSubscription = interval(10000).subscribe(() => {
        this.search();
      });
    }
    this.enableLiveUpdate = !this.enableLiveUpdate;
  }

  onPageChange(newPage: number): void {
    this.currentPage = newPage;
    this.search();
  }

  filterMethods() {
    this.highlightedMethodIndex = -1;
    const value = this.searchModel.method.toLowerCase();
    this.filteredMethods = this.allowedMethods.filter((m) => m.toLowerCase().includes(value));
  }

  selectOption(method: string) {
    this.searchModel.method = method;
    this.showOptions = false;
  }

  selectTypedValue() {
    this.showOptions = false;
  }

  hideOptions() {
    setTimeout(() => (this.showOptions = false), 150);
  }

  onKeyDown(event: KeyboardEvent) {
    if (!this.showOptions || this.filteredMethods.length === 0) return;

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        this.highlightedMethodIndex =
          (this.highlightedMethodIndex + 1) % this.filteredMethods.length;
        this.scrollHighlightedIntoView();
        break;

      case 'ArrowUp':
        event.preventDefault();
        this.highlightedMethodIndex =
          (this.highlightedMethodIndex - 1 + this.filteredMethods.length) %
          this.filteredMethods.length;
        this.scrollHighlightedIntoView();
        break;

      case 'Enter':
        if (this.highlightedMethodIndex >= 0) {
          event.preventDefault();
          this.selectOption(this.filteredMethods[this.highlightedMethodIndex]);
        }
        break;
    }
  }

  scrollHighlightedIntoView() {
    setTimeout(() => {
      const el = document.querySelector('.method-item.active');
      el?.scrollIntoView({ block: 'nearest' });
    });
  }

  selectRealm(realm: string) {
    this.ls.setItem('selectedRealm', realm);
    this.ls.setItem('agRealm', realm);
    window.location.reload();
  }
}
